import { ROLES } from 'config/roles';
import { EXTERNAL_PLATFORMS } from 'config/externalPlatforms';
import { PATHS } from 'routes/routePaths';

const { sandbox, simulation } = EXTERNAL_PLATFORMS;

/**
 * Role-driven sidebar navigation. Each role sees only its own items; the
 * dashboard is the live destination today, with `soon` placeholders marking
 * where future feature pages will slot in.
 *
 * Icons are referenced by string key (resolved by `NavIcon` in ./icons) so this
 * stays plain, serializable config rather than carrying JSX. An `external: true`
 * item renders as a plain link (new tab) instead of an in-app route (see
 * NavList) — used for the Sandbox and simulation platforms, which are separate
 * sites (`config/externalPlatforms`). `tab`/`tabLabel`/`tabMatch` drive the
 * phone tab bar (MobileTabBar) for roles that have one.
 */
export const NAV_BY_ROLE = {
  // Every student page has its own rail item, as before the Oct 2026
  // redesign. `tab: true` marks the five that also make the phone tab bar
  // (MobileTabBar), `tabLabel` is their short label there, and `tabMatch`
  // keeps a tab lit on the related pages that aren't tabs themselves.
  [ROLES.STUDENT]: [
    { label: 'Dashboard', to: PATHS.STUDENT_DASHBOARD, icon: 'home', tab: true, tabLabel: 'Home' },
    {
      label: 'My classes',
      to: PATHS.STUDENT_CLASSES,
      icon: 'classes',
      tab: true,
      tabLabel: 'Classes',
      tabMatch: [PATHS.STUDENT_CALENDAR, PATHS.STUDENT_REQUESTS, PATHS.STUDENT_MENTOR, PATHS.STUDENT_AVAILABILITY],
    },
    { label: 'Calendar', to: PATHS.STUDENT_CALENDAR, icon: 'calendar' },
    {
      label: 'My progress',
      to: PATHS.STUDENT_PROGRESS,
      icon: 'chart',
      tab: true,
      tabLabel: 'Progress',
      tabMatch: [PATHS.STUDENT_RECORDS],
    },
    { label: 'Requests', to: PATHS.STUDENT_REQUESTS, icon: 'swap' },
    { label: 'My availability', to: PATHS.STUDENT_AVAILABILITY, icon: 'clock' },
    { label: 'Session log', to: PATHS.STUDENT_RECORDS, icon: 'records' },
    { label: 'Resources', to: PATHS.STUDENT_RESOURCES, icon: 'library', tab: true, tabLabel: 'Library' },
    { label: 'My projects', to: PATHS.STUDENT_PORTFOLIO, icon: 'briefcase', tab: true, tabLabel: 'Projects' },
    { label: 'My mentors', to: PATHS.STUDENT_MENTOR, icon: 'users' },
    { label: sandbox.label, to: sandbox.url, icon: sandbox.navIcon, external: true },
    { label: simulation.label, to: simulation.url, icon: simulation.navIcon, external: true },
    { label: 'Settings', icon: 'settings', soon: true },
  ],
  [ROLES.MENTOR]: [
    { label: 'Dashboard', to: PATHS.MENTOR_DASHBOARD, icon: 'home' },
    // `nested`: stays lit on a student's own page (/mentor/students/:id).
    { label: 'My students', to: PATHS.MENTOR_STUDENTS, icon: 'students', nested: true },
    { label: 'Classes', to: PATHS.MENTOR_CLASSES, icon: 'classes' },
    { label: 'Calendar', to: PATHS.MENTOR_CALENDAR, icon: 'calendar' },
    { label: 'Requests', to: PATHS.MENTOR_REQUESTS, icon: 'swap' },
    { label: 'My availability', to: PATHS.MENTOR_AVAILABILITY, icon: 'clock' },
    { label: 'Open slots', to: PATHS.MENTOR_SLOTS, icon: 'slot' },
    { label: 'Session log', to: PATHS.MENTOR_RECORDS, icon: 'records' },
    { label: 'Payouts', to: PATHS.MENTOR_PAYOUTS, icon: 'wallet' },
    { label: 'Resources', to: PATHS.MENTOR_RESOURCES, icon: 'library' },
    { label: 'Project reviews', to: PATHS.MENTOR_PORTFOLIO, icon: 'review' },
    { label: sandbox.label, to: sandbox.url, icon: sandbox.navIcon, external: true },
    { label: simulation.label, to: simulation.url, icon: simulation.navIcon, external: true },
    { label: 'Settings', icon: 'settings', soon: true },
  ],
  [ROLES.ADMIN]: [
    { label: 'Dashboard', to: PATHS.ADMIN_DASHBOARD, icon: 'home' },
    // The primary intake path — everything below it is for editing what exists.
    { label: 'Enrol a student', to: PATHS.ADMIN_ENROL, icon: 'graduation' },
    { label: 'Students', to: PATHS.ADMIN_STUDENTS, icon: 'graduation' },
    { label: 'Mentors', to: PATHS.ADMIN_MENTORS, icon: 'users' },
    { label: 'Accounts', to: PATHS.ADMIN_ACCOUNTS, icon: 'shield' },
    { label: 'Courses', to: PATHS.ADMIN_COURSES, icon: 'library' },
    { label: 'Presentations', to: PATHS.ADMIN_PRESENTATIONS, icon: 'slides' },
    { label: 'Enrollments', to: PATHS.ADMIN_ENROLLMENTS, icon: 'book' },
    { label: 'Assign mentors', to: PATHS.ADMIN_ASSIGN, icon: 'users' },
    { label: 'Schedule class', to: PATHS.ADMIN_SCHEDULE, icon: 'slot' },
    { label: 'Reschedule', to: PATHS.ADMIN_RESCHEDULE, icon: 'swap' },
    { label: 'Calendar', to: PATHS.ADMIN_CALENDAR, icon: 'calendar' },
    { label: 'Session logs', to: PATHS.ADMIN_SESSION_LOGS, icon: 'records' },
    { label: 'Disputes', to: PATHS.ADMIN_DISPUTES, icon: 'flag' },
    { label: 'Reports', to: PATHS.ADMIN_REPORTS, icon: 'chart' },
    { label: sandbox.label, to: sandbox.url, icon: sandbox.navIcon, external: true },
    { label: 'Settings', to: PATHS.ADMIN_SETTINGS, icon: 'settings' },
  ],
};
